import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { GET_TRIP_REPORT, GET_MASTER_BOAT_ROUTES, GET_BOAT_ROUTE_TRIP_STATUS } from 'graphql/reports/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import { Input } from 'components/input'
import { Badge } from 'components/badge'
import { Divider } from 'components'
import loadingImage from '../../assets/images/boat-loading.gif'
import i18n from 'i18next'
import { UserListDropdown } from 'components/custom/userListDropdown'
import { Select } from 'components/select'
import { MultiSelect } from 'components/multi-select'
import { TripsIcon } from 'components/icons'

const INITIAL_FILTER_STATE = {
    trip_date: {
        start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    },
    status_code: '',
    route_ids: [],
    captain_id: '',
}

export default function TripReport() {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [routeOptions, setRouteOptions] = useState([])
    const [routeLoading, setRouteLoading] = useState(false)
    const [statusOptions, setStatusOptions] = useState([])
    const [statusLoading, setStatusLoading] = useState(false)
    const [filterApplied, setFilterApplied] = useState(false)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setData([])
            setFilterApplied(false)
        }
    }, [])

    const [getTripReport, { loading: reportLoading }] = useLazyQuery(GET_TRIP_REPORT, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.tripReport.success && data.tripReport.code === '200') {
                setData(data.tripReport.trip_report)
                setTotalRows(data.tripReport.total_count)
            } else {
                handleError(data.tripReport.message)
            }
        },
        onError: error => handleError(error.message),
    })

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch trip report'))
        setShowErrDialog(true)
    }

    // Add query to fetch boat routes
    const [getMasterBoatRoutes] = useLazyQuery(GET_MASTER_BOAT_ROUTES, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getMasterBoatRoute.success && data.getMasterBoatRoute.code === '200') {
                const options = data.getMasterBoatRoute.master_boat_routes.map(route => ({
                    value: route.master_route_id,
                    label: route.route_name.en || route.route_name.ar,
                }))
                setRouteOptions(options)
            } else {
                handleError(data.getMasterBoatRoute.message)
            }
            setRouteLoading(false)
        },
        onError: error => {
            handleError(error.message)
            setRouteLoading(false)
        },
    })

    // Fetch route options on component mount
    useEffect(() => {
        setRouteLoading(true)
        getMasterBoatRoutes({
            variables: {
                page: {
                    offset: 0,
                    limit: 100,
                },
            },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run once on mount - lazy queries are stable

    // Add query to fetch trip statuses
    const [getBoatRouteTripStatus] = useLazyQuery(GET_BOAT_ROUTE_TRIP_STATUS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.boatRouteTripStatus && Array.isArray(data.boatRouteTripStatus)) {
                setStatusOptions(data.boatRouteTripStatus)
            }
            setStatusLoading(false)
        },
        onError: error => {
            handleError(error.message)
            setStatusLoading(false)
        },
    })

    // Fetch status options on component mount
    useEffect(() => {
        setStatusLoading(true)
        getBoatRouteTripStatus({
            variables: {
                code: '',
            },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run once on mount - lazy queries are stable

    // Modify fetchTripReport to receive filterData as a parameter and use the new filter structure
    const fetchTripReport = (page, filterData = filterObj, limit = perPage) => {
        const offset = (page - 1) * limit
        getTripReport({
            variables: {
                page: {
                    limit,
                    offset,
                },
                tripFilter: {
                    trip_date: {
                        start_date: filterData.trip_date.start_date,
                        end_date: filterData.trip_date.end_date,
                    },
                    status_code: filterData.status_code || null,
                    route_ids: filterData.route_ids?.length > 0 ? filterData.route_ids : null,
                    captain_id: filterData.captain_id || null,
                },
            },
        })
    }

    useEffect(() => {
        if (filterApplied) {
            fetchTripReport(currentPage, filterObj, perPage)
        }
    }, [currentPage, perPage, filterApplied])

    const handlePageChange = page => {
        if (filterApplied) {
            setCurrentPage(page)
        }
    }

    const handlePerRowsChange = newPerPage => {
        if (newPerPage !== perPage && filterApplied) {
            setPerPage(newPerPage)
            setCurrentPage(1)
        }
    }

    const handleFilter = () => {
        setCurrentPage(1)
        setFilterApplied(true)
        fetchTripReport(1, filterObj)
    }

    const handleClear = () => {
        const resetFilter = {
            trip_date: {
                start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
                end_date: moment().format('YYYY-MM-DD'),
            },
            status_code: '',
            route_ids: [],
            captain_id: '',
        }
        setFilterObj(resetFilter)
        setCurrentPage(1)
        setPerPage(10)
        setFilterApplied(false)
        setData([]) // Clear existing data
    }

    const fetchBatch = async (offset, limit, filterData = filterObj) => {
        try {
            const result = await getTripReport({
                variables: {
                    page: {
                        limit,
                        offset,
                    },
                    tripFilter: {
                        trip_date: {
                            start_date: filterData.trip_date.start_date,
                            end_date: filterData.trip_date.end_date,
                        },
                        status_code: filterData.status_code || null,
                        route_ids: filterData.route_ids?.length > 0 ? filterData.route_ids : null,
                        captain_id: filterData.captain_id || null,
                    },
                },
            })

            if (result.data?.tripReport?.success && result.data.tripReport.code === '200') {
                return {
                    data: result.data.tripReport.trip_report,
                    total: result.data.tripReport.total_count,
                }
            } else {
                throw new Error(result.data?.tripReport?.message || 'Failed to fetch data')
            }
        } catch (error) {
            throw error
        }
    }

    // Separate function for fetching data for export
    const [getTripReportForExport] = useLazyQuery(GET_TRIP_REPORT, {
        fetchPolicy: 'network-only',
        onError: error => {
            handleError(error.message)
            setExportLoading(false)
        },
    })

    const fetchBatchForExport = async (offset, limit, filterData = filterObj) => {
        try {
            const result = await getTripReportForExport({
                variables: {
                    page: {
                        limit,
                        offset,
                    },
                    tripFilter: {
                        trip_date: {
                            start_date: filterData.trip_date.start_date,
                            end_date: filterData.trip_date.end_date,
                        },
                        status_code: filterData.status_code || null,
                        route_ids: filterData.route_ids?.length > 0 ? filterData.route_ids : null,
                        captain_id: filterData.captain_id || null,
                    },
                },
            })

            if (result.data?.tripReport?.success && result.data.tripReport.code === '200') {
                return {
                    data: result.data.tripReport.trip_report,
                    total: result.data.tripReport.total_count,
                }
            } else {
                throw new Error(result.data?.tripReport?.message || 'Failed to fetch data')
            }
        } catch (error) {
            throw error
        }
    }

    const handleExportCSV = async () => {
        try {
            setExportLoading(true)

            // Create a copy of the current filterObj state
            const currentFilterObj = { ...filterObj }

            // Get initial batch to get total count
            const initialBatch = await fetchBatchForExport(0, 100, currentFilterObj)
            const totalRecords = initialBatch.total

            if (totalRecords === 0) {
                handleError(t('No records found to export'))
                return
            }

            let allData = [...initialBatch.data]
            let offset = 100

            // Continue fetching if there are more records
            while (allData.length < totalRecords) {
                const batchData = await fetchBatchForExport(offset, 100, currentFilterObj)
                allData = [...allData, ...batchData.data]
                offset += 100
            }

            // Generate single CSV
            const headers = [
                'Trip Date',
                'Trip ID',
                'Route ID',
                'Route Name',
                'Scheduled Time',
                'Actual Time',
                'Captain 1 Name',
                'Captain 2 Name',
                'Status',
            ]

            const csvContent = [
                headers.join(','),
                ...allData.map(row =>
                    [
                        row.trip_date,
                        row.trip_id,
                        row.route_id,
                        row.route_name.en || row.route_name.ar,
                        row.scheduled_time,
                        row.actual_time,
                        row.captain_name,
                        row.captain_name_2,
                        row.status.en || row.status.ar,
                    ].join(',')
                ),
            ].join('\n')

            // Create and download single file
            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `trip-report-${moment().format('YYYY-MM-DD')}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    const columns = useMemo(() => [
        {
            name: t('Trip Date'),
            selector: row => row.trip_date,
            sortable: true,
            cell: row => moment(row.trip_date).format('DD MMM YYYY'),
        },

        {
            name: t('Route Name'),
            selector: row => row.route_name.en || row.route_name.ar,
            sortable: true,
        },
        {
            name: t('Scheduled Time'),
            selector: row => moment(row.scheduled_time).format('hh:mm a'), // Show only time
            sortable: true,
        },
        {
            name: t('Actual Time'),
            selector: row => moment(row.actual_time).format('hh:mm a'), // Show only time
            sortable: true,
        },
        {
            name: t('Captain 1 Name'),
            selector: row => row.captain_name,
            sortable: true,
        },
        {
            name: t('Captain 2 Name'),
            selector: row => row.captain_name_2,
            sortable: true,
        },
        {
            name: t('Status'),
            selector: row => row.status.en || row.status.ar,
            cell: row => (
                <Badge
                    color={
                        row.status.en === 'Scheduled'
                            ? 'orange'
                            : row.status.en === 'Canceled'
                            ? 'pink'
                            : row.status.en === 'Active'
                            ? 'emerald'
                            : 'zinc'
                    }
                    className="capitalize"
                >
                    {row.status.en || '-'}
                </Badge>
            ),
            sortable: true,
        },
    ])

    // Custom table styles with auto width and RTL support
    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f9fafb',
                color: '#111827',
                fontWeight: 600,
                fontSize: '0.875rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto', // Let columns determine their width
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        cells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto', // Let columns determine their width
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        pagination: {
            style: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
            },
            pageButtonsStyle: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                marginLeft: i18n.language === 'ar' ? '0' : '0.5rem',
                marginRight: i18n.language === 'ar' ? '0.5rem' : '0',
            },
        },
        noData: {
            style: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
            },
        },
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <TripsIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Trip Report')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Detailsed list of all trips taken')}
                            </p>
                        </div>
                    </h1>

                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Trip Date')}</label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(1, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.trip_date.start_date, filterObj.trip_date.end_date]}
                                onChange={dates => {
                                    setFilterObj({
                                        ...filterObj,
                                        trip_date: {
                                            start_date: dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null,
                                            end_date: dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null,
                                        },
                                    })
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-0">{t('Route')}</label>
                            {!routeLoading ? (
                                <MultiSelect
                                    placeholder={t('All')}
                                    value={filterObj.route_ids || []}
                                    onChange={(values) => setFilterObj({ ...filterObj, route_ids: values })}
                                    options={routeOptions}
                                    disabled={routeLoading}
                                />
                            ) : (
                                <p className="text-sm text-gray-500 mt-1">{t('Loading routes...')}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Captain ID')}</label>
                            <UserListDropdown
                                userType="captain"
                                fields={{
                                    name: 'captainId',
                                    id: 'captainId',
                                    value: filterObj.captain_id,
                                    onChange: e => {
                                        setFilterObj({ ...filterObj, captain_id: e.target.value })
                                    },
                                }}
                                purpose="add"
                                isActive={true}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-0">{t('Status Code')}</label>
                            {!statusLoading ? (
                                <Select
                                    id="statusCode"
                                    name="statusCode"
                                    value={filterObj.status_code}
                                    onChange={e => setFilterObj({ ...filterObj, status_code: e.target.value })}
                                    disabled={statusLoading}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                                >
                                    <option value="">{t('All')}</option>
                                    {statusOptions.map(status => (
                                        <option key={status.code} value={status.code}>
                                            {i18n.language === 'ar' && status.description.ar
                                                ? status.description.ar
                                                : status.description.en}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <p className="text-sm text-gray-500 mt-1">{t('Loading statuses...')}</p>
                            )}
                        </div>

                        <div className="sm:col-span-4 justify-end flex items-end gap-2">
                            <button
                                onClick={handleFilter}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Filter')}
                            </button>
                            <button
                                onClick={handleClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="my-4 mb-6" />

            {reportLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4 -mt-3">
                        <p className="font-semibold">Trip Reports</p>
                        {data.length > 0 && (
                            <button
                                onClick={handleExportCSV}
                                disabled={exportLoading}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {exportLoading ? (
                                    <>
                                        <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                        {t('Exporting...')}
                                    </>
                                ) : (
                                    <>
                                        <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                        {t('Export to CSV')}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    <DataTable
                        columns={columns}
                        data={data}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        paginationDefaultPage={currentPage}
                        paginationRowsPerPageOptions={[10, 20, 50]}
                        paginationPerPage={perPage}
                        customStyles={customStyles} // Add custom styles here
                        noDataComponent={
                            <div className="text-center py-24 bg-gray-50 mt-0 rounded-lg w-full">
                                <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    className="mx-auto h-12 w-12 text-gray-400"
                                >
                                    <path
                                        d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                        strokeWidth={2}
                                        vectorEffect="non-scaling-stroke"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t('Please add or modify your search criteria and try again.')}
                                </p>
                                <div className="mt-6"></div>
                            </div>
                        }
                    />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
